import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'

interface CarouselSlide {
  image: string
  title: string
  subtitle?: string
}

interface HeroCarouselProps {
  slides: CarouselSlide[]
  autoPlayInterval?: number
}

const HeroCarousel = ({ slides, autoPlayInterval = 5000 }: HeroCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [direction, setDirection] = useState(0)

  // Auto-play functionality
  useEffect(() => {
    if (slides.length <= 1) return

    const interval = setInterval(() => {
      setDirection(1)
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length)
    }, autoPlayInterval)

    return () => clearInterval(interval)
  }, [slides.length, autoPlayInterval])

  const goToSlide = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1)
    setCurrentIndex(index)
  }

  const goToPrevious = () => {
    setDirection(-1)
    setCurrentIndex((prevIndex) => (prevIndex - 1 + slides.length) % slides.length)
  }

  const goToNext = () => {
    setDirection(1)
    setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length)
  }

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0,
    }),
  }

  return (
    <div className="relative w-full h-[50vh] sm:h-[55vh] md:h-[60vh] min-h-[400px] sm:min-h-[500px] max-h-[600px] sm:max-h-[700px] overflow-hidden">
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'tween', duration: 0.5, ease: 'easeInOut' },
            opacity: { duration: 0.3 },
          }}
          className="absolute inset-0 w-full h-full"
          style={{ willChange: 'transform, opacity' }}
        >
          {/* Image */}
          <div className="relative w-full h-full overflow-hidden">
            <img
              src={slides[currentIndex].image}
              alt={slides[currentIndex].title}
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center center' }}
            />
            
            {/* Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-dark-950/80 via-dark-950/40 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-dark-950/70 via-transparent to-dark-950/50"></div>
            
            {/* Animated Gradient Overlay */}
            <motion.div
              animate={{
                background: [
                  'radial-gradient(circle at 20% 50%, rgba(33, 150, 243, 0.15) 0%, transparent 50%)',
                  'radial-gradient(circle at 80% 50%, rgba(176, 38, 255, 0.15) 0%, transparent 50%)',
                  'radial-gradient(circle at 20% 50%, rgba(33, 150, 243, 0.15) 0%, transparent 50%)',
                ],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute inset-0"
              style={{ willChange: 'background' }}
            />

            {/* Content */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="container-custom px-4 sm:px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="text-center max-w-4xl mx-auto"
                >
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="relative text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-3 sm:mb-4 leading-tight px-4"
                  >
                    {/* Glow effect behind text */}
                    <motion.div
                      className="absolute -inset-8 blur-3xl opacity-30"
                      animate={{
                        background: [
                          'radial-gradient(circle, rgba(33, 150, 243, 0.4) 0%, transparent 70%)',
                          'radial-gradient(circle, rgba(176, 38, 255, 0.4) 0%, transparent 70%)',
                          'radial-gradient(circle, rgba(33, 150, 243, 0.4) 0%, transparent 70%)',
                        ],
                      }}
                      transition={{
                        duration: 5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                      style={{ willChange: 'background' }}
                    />
                    <span className="relative text-gradient bg-clip-text text-transparent bg-gradient-to-r from-white via-primary-200 via-purple-200 to-white drop-shadow-2xl">
                      {slides[currentIndex].title}
                    </span>
                  </motion.h1>
                  {slides[currentIndex].subtitle && (
                    <motion.p
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.6 }}
                      className="text-lg sm:text-xl md:text-2xl text-gray-200 font-medium px-4"
                    >
                      {slides[currentIndex].subtitle}
                    </motion.p>
                  )}
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <motion.button
            onClick={goToPrevious}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="absolute left-2 sm:left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 rounded-full bg-dark-900/80 hover:bg-dark-800/90 backdrop-blur-md border border-dark-700/50 hover:border-primary-500/50 transition-all duration-300 group shadow-2xl"
            aria-label="Previous slide"
          >
            <FaChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-300 group-hover:text-primary-400 transition-colors" />
          </motion.button>
          <motion.button
            onClick={goToNext}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="absolute right-2 sm:right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 rounded-full bg-dark-900/80 hover:bg-dark-800/90 backdrop-blur-md border border-dark-700/50 hover:border-primary-500/50 transition-all duration-300 group shadow-2xl"
            aria-label="Next slide"
          >
            <FaChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-300 group-hover:text-primary-400 transition-colors" />
          </motion.button>
        </>
      )}

      {/* Indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-dark-900/60 backdrop-blur-md border border-dark-700/50">
          {slides.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => goToSlide(index)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              className={`relative transition-all duration-300 ${
                index === currentIndex ? 'w-10' : 'w-3'
              } h-3 rounded-full overflow-hidden group`}
              aria-label={`Go to slide ${index + 1}`}
            >
              <div
                className={`absolute inset-0 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-primary-500 shadow-lg shadow-primary-500/50'
                    : 'bg-gray-600 group-hover:bg-primary-400/50'
                }`}
              />
              {index === currentIndex && (
                <motion.div
                  layoutId="indicator"
                  className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-400 rounded-full"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </motion.button>
          ))}
        </div>
      )}

      {/* Progress Bar */}
      {slides.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-dark-900/50 z-20">
          <motion.div
            key={currentIndex}
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: autoPlayInterval / 1000, ease: 'linear' }}
            className="h-full bg-gradient-to-r from-primary-500 to-primary-400"
          />
        </div>
      )}
    </div>
  )
}

export default HeroCarousel
